import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import * as Tokens from '@shared/core/tokens';
import * as Utils from '@shared/core/utils';
import * as Mappers from '@shared/core/mappers';

import { CryptoService } from './crypto.shared.service';

import { Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take } from 'rxjs/operators';

export interface JwtPayload {
    authct: string;
    c_token_ident: string;
    email: string;
    exp: number;
    iat: number;
    iss: string;
    nameid: string;
    nbf: number;
    unique_name: string;
}

@Injectable({
    providedIn: 'root',
})
export class JWTService {
    public readonly apiBaseUrl: string = this._config.api.base;
    public readonly apiKey: string = this._config.api.key;

    private readonly _enableLogging: boolean = false;

    private _refreshTokenSubject: BehaviorSubject<OLO.DTO.JWTokenObject> = new BehaviorSubject(null);
    private _isRefreshing = false;

    constructor(@Inject(Tokens.CONFIG_TOKEN) private _config: OLO.Config, private _httpClient: HttpClient, private _cryptoService: CryptoService) {}

    public reqestJWToken(memberCredentials: OLO.DTO.JWTokenRequest): Observable<boolean> {
        const postModel: { Email: string; Password: string } = Mappers.JWTMapper.mapReqestJWToken(memberCredentials);

        return this._httpClient.post<OLO.DTO.JWTokenObject>(`${Utils.HTTP.switchApi(this.apiBaseUrl)}/auth/login/member`, postModel).pipe(
            map((response: APIv3.JwtTokenResponse) => Mappers.JWTMapper.mapPostJWTokenResponse(response)),
            map(this._setupTokens.bind(this)),
        );
    }

    public setJWToken(tokens: OLO.DTO.JWTokenObject): Observable<OLO.DTO.JWTokenObject> {
        return of(this._setupTokens(tokens));
    }

    public getCurrentTokens(): Observable<OLO.DTO.JWTokenObject> {
        const tokens = this._getTokens();

        if (!tokens.AccessToken) {
            return of({ AccessToken: null, RefreshToken: null });
        }

        if (!this._validateToken(tokens)) {
            return this._getRefreshedToken().pipe(switchMap(() => of(this._getTokens())));
        }

        return of(tokens).pipe(take(1));
    }

    private _getRefreshedToken(): Observable<OLO.DTO.JWTokenObject> {
        if (this._isRefreshing) {
            return this._refreshTokenSubject.asObservable();
        } else {
            this._isRefreshing = true;
            const tokens = this._getTokens();

            return this.refreshJWToken(tokens.AccessToken, tokens.RefreshToken).pipe(
                switchMap((token) => {
                    this._isRefreshing = false;
                    this._refreshTokenSubject.next(token);

                    return of(token);
                }),
                catchError((error) => {
                    this._isRefreshing = false;

                    return null;
                }),
            );
        }
    }

    public refreshJWToken(currentJWToken: string, refreshToken: string, clientAppKey: string = this.apiKey): Observable<OLO.DTO.JWTokenObject> {
        const url = `${Utils.HTTP.switchApi(this.apiBaseUrl)}/auth/refreshToken/${Utils.Strings.removeQuotesChars(refreshToken)}`;
        const body = null;
        const pathAndQuery = Utils.HTTP.getPathAndQuery(url);

        return this._httpClient
            .post<OLO.DTO.JWTokenObject>(url, body, {
                headers: new HttpHeaders()
                    .set('ClientAppKey', `${Utils.Strings.removeQuotesChars(clientAppKey)}`)
                    .set('clientAppSignature', Utils.HTTP.getClientAppSignature(pathAndQuery, body, this._config.api.key))
                    .set('Authorization', `Bearer ${Utils.Strings.removeQuotesChars(currentJWToken)}`)
                    .set('authExempt', 'true'),
            })
            .pipe(
                debounceTime(300),
                map((response: APIv3.JwtTokenResponse) => Mappers.JWTMapper.mapPostJWTokenResponse(response)),
                map((tokens) => this._setupTokens(tokens)),
            );
    }

    private _setupTokens(tokens: OLO.DTO.JWTokenObject): OLO.DTO.JWTokenObject {
        try {
            this._storeTokens(this._encrypt(tokens));

            if (this._enableLogging) console.log('JWT tokens setup');

            return tokens;
        } catch (ex) {
            if (this._enableLogging) console.warn('Tokens setup error: ', ex);

            return null;
        }
    }

    private _getTokens(): OLO.DTO.JWTokenObject {
        try {
            const encrypted: OLO.DTO.JWTokenObject = JWTService._getEncryptedTokens();

            if (!encrypted.AccessToken || !encrypted.RefreshToken) {
                throwError('Tokens not found in storage.');
            }

            return this._decrypt(encrypted);
        } catch (ex) {
            if (this._enableLogging) console.error('Error getting tokens: ', ex);

            return null;
        }
    }

    /* store/encryption helpers */

    private _encrypt(tokens: OLO.DTO.JWTokenObject): OLO.DTO.JWTokenObject {
        return {
            AccessToken: this._cryptoService.encrypt(tokens.AccessToken),
            RefreshToken: this._cryptoService.encrypt(tokens.RefreshToken),
        };
    }

    private _storeTokens(tokens: OLO.DTO.JWTokenObject): void {
        Utils.Storage.set(OLO.Enums.JWT.ACCESS_TOKEN as unknown as string, tokens.AccessToken);
        Utils.Storage.set(OLO.Enums.JWT.REFRESH_TOKEN as unknown as string, tokens.RefreshToken);
    }

    private static _getEncryptedTokens(): OLO.DTO.JWTokenObject {
        return {
            AccessToken: Utils.Storage.getItem(OLO.Enums.JWT.ACCESS_TOKEN as unknown as string),
            RefreshToken: Utils.Storage.getItem(OLO.Enums.JWT.REFRESH_TOKEN as unknown as string),
        };
    }

    private _decrypt(tokens: OLO.DTO.JWTokenObject): OLO.DTO.JWTokenObject {
        return {
            AccessToken: this._cryptoService.decrypt(tokens.AccessToken),
            RefreshToken: this._cryptoService.decrypt(tokens.RefreshToken),
        };
    }

    private _validateToken(tokens: OLO.DTO.JWTokenObject): boolean {
        const currentTime = new Date();
        const decryptedJWT = this._parseJwt(tokens.AccessToken);
        const expiredDate = new Date(decryptedJWT.exp * 1000);

        return Utils.Dates.datesDiffInSecs(expiredDate, currentTime) > 0;
    }

    public clearTokens(): void {
        if (this._enableLogging) console.log('clear jwt tokens');
        Utils.Storage.remove(OLO.Enums.JWT.ACCESS_TOKEN as unknown as string);
        Utils.Storage.remove(OLO.Enums.JWT.REFRESH_TOKEN as unknown as string);
    }

    private _parseJwt(token: string): JwtPayload {
        const [, base64Url] = token.split('.');
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

        const decodedData = atob(base64);
        const uriEncodedData = Array.from(decodedData)
            .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join('');

        const jsonPayload = decodeURIComponent(uriEncodedData);

        return JSON.parse(jsonPayload);
    }
}
